import { useCallback, useEffect, useRef, useState } from "react";

export function useHorizontalSlider(itemCount) {
  const trackRef = useRef(null);
  const [canGoBack, setCanGoBack] = useState(false);
  const [canGoForward, setCanGoForward] = useState(false);

  const updateControls = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    setCanGoBack(track.scrollLeft > 2);
    setCanGoForward(track.scrollLeft < maximumScroll - 2);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    updateControls();
    track.addEventListener("scroll", updateControls, { passive: true });

    const observer =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(updateControls)
        : null;

    if (observer) {
      observer.observe(track);
    } else {
      window.addEventListener("resize", updateControls);
    }

    return () => {
      track.removeEventListener("scroll", updateControls);
      observer?.disconnect();
      if (!observer) {
        window.removeEventListener("resize", updateControls);
      }
    };
  }, [itemCount, updateControls]);

  const move = useCallback((direction) => {
    const track = trackRef.current;
    const firstItem = track?.querySelector("[data-slider-item]");
    if (!track || !firstItem) return;

    const styles = window.getComputedStyle(track);
    const gap = Number.parseFloat(styles.columnGap || styles.gap) || 0;
    const distance = firstItem.getBoundingClientRect().width + gap;

    track.scrollBy({
      left: direction * distance,
      behavior: "smooth",
    });
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        move(-1);
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        move(1);
      }
    },
    [move],
  );

  return {
    trackRef,
    canGoBack,
    canGoForward,
    goBack: () => move(-1),
    goForward: () => move(1),
    handleKeyDown,
  };
}
